interface SettingsCheckboxGroupProps<T extends string> {
    legend: string;
    description?: string;
    options: readonly T[];
    selected: T[];
    onToggle: (value: T) => void;
    onSelectAll: () => void;
    onClearAll: () => void;
    name: string;
}

export default function SettingsCheckboxGroup<T extends string>({
    legend,
    description,
    options,
    selected,
    onToggle,
    onSelectAll,
    onClearAll,
    name,
}: SettingsCheckboxGroupProps<T>) {
    const blockedCount = selected.length;

    return (
        <fieldset className="px-4 py-3.5">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <legend className="text-sm font-medium text-[#1e293b]">{legend}</legend>
                    {description && (
                        <p className="text-xs text-[#64748b] mt-0.5">{description}</p>
                    )}
                    <p className="text-xs text-[#94a3b8] mt-1">
                        {blockedCount === 0
                            ? 'All roles can access'
                            : `${blockedCount} role${blockedCount === 1 ? '' : 's'} restricted`}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onSelectAll}
                        className="text-xs font-medium text-[#6e64f0] hover:text-[#5b53d4] transition-colors"
                    >
                        Select all
                    </button>
                    <span className="text-[#cbd5e1]">·</span>
                    <button
                        type="button"
                        onClick={onClearAll}
                        className="text-xs font-medium text-[#64748b] hover:text-[#475569] transition-colors"
                    >
                        Clear
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {options.map((option) => {
                    const inputId = `${name}-${option}`;
                    const isChecked = selected.includes(option);

                    return (
                        <label
                            key={option}
                            htmlFor={inputId}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                                isChecked
                                    ? 'border-[#c4b5fd] bg-[#f5f3ff] text-[#5b21b6]'
                                    : 'border-[#e2e8f0] bg-[#f8fafc] text-[#475569] hover:border-[#cbd5e1]'
                            }`}
                        >
                            <input
                                id={inputId}
                                type="checkbox"
                                name={name}
                                checked={isChecked}
                                onChange={() => onToggle(option)}
                                className="h-3.5 w-3.5 rounded border-[#cbd5e1] text-[#6e64f0] focus:ring-[#6e64f0]"
                            />
                            <span className="text-xs font-medium truncate">{option}</span>
                        </label>
                    );
                })}
            </div>
        </fieldset>
    );
}
